import { StackLayout } from '@euler/app/components/StackLayout';
import { IconProps } from '@euler/app/components/icons/types';
import { useSvgProps } from '@euler/app/components/icons/util';
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { useCanRollbackOrder } from '@euler/app/flows/record/functions/useRollbackOrder';
import { MediasIcon } from '@euler/app/flows/record/pages/detail/components/menu-icons/MediasIcon';
import { PendingIssuesIcon } from '@euler/app/flows/record/pages/detail/components/menu-icons/PendingIssuesIcon';
import { ProductManifestIcon } from '@euler/app/flows/record/pages/detail/components/menu-icons/ProductManifestIcon';
import { QuotationIcon } from '@euler/app/flows/record/pages/detail/components/menu-icons/QuotationIcon';
import { RecordsIcon } from '@euler/app/flows/record/pages/detail/components/menu-icons/RecordsIcon';
import { ReportsIcon } from '@euler/app/flows/record/pages/detail/components/menu-icons/ReportsIcon';
import { RollbackIcon } from '@euler/app/flows/record/pages/detail/components/menu-icons/RollbackIcon';
import { ServiceRecordsIcon } from '@euler/app/flows/record/pages/detail/components/menu-icons/ServiceRecordsIcon';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useRemoveProductManifestItem } from '@euler/app/flows/task/functions/products/useRemoveProductManifestItem';
import { useTaskExtraInfo } from '@euler/app/flows/task/functions/useTaskExtraInfo';
import { useTaskNavigation } from '@euler/app/flows/task/functions/useTaskNavigation';
import { useViewQuotation } from '@euler/app/flows/task/functions/useViewQuotation';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { MaybeText } from '@euler/components/MaybeText';
import { useAppLoading } from '@euler/components/loading/Provider';
import { FontFamily } from '@euler/components/typography';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import { orgMemberToProductAuthor } from '@euler/functions/orgMemberToProductAuthor';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrgMember } from '@euler/model/entity';
import {
  CommonTaskStatus,
  InspectionTaskStatus,
  StoreFeature,
  TaskProductManifestItemOrigin,
} from '@euler/model/enum';
import { TaskProductManifestItem } from '@euler/model/task-detail';
import { ReportTabKey } from '@euler/model/viewmodel';
import { safeMarkDevFlag, showAlert } from '@euler/utils';
import {
  useBehaviorSubject,
  useLocalObservable,
  usePersistFn,
} from '@euler/utils/hooks';
import { FC, ReactNode, memo, useEffect } from 'react';
import {
  StyleProp,
  Text,
  TextStyle,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import { Path, Svg } from 'react-native-svg';

const kForceQuotationMenuItem = safeMarkDevFlag(false);
const kForceProductManifestMenuItem = safeMarkDevFlag(false);

export const TaskDetailMenu = memo(
  ({ serviceRecordCount }: { serviceRecordCount: number }) => {
    const { rpx } = useResponsiveContext();
    const { event$ } = useOrderContext();

    const navigation = useTaskNavigation();

    const removeProductManifestItem = useRemoveProductManifestItem({
      origin: {
        type: TaskProductManifestItemOrigin.TaskDetail,
      },
    });

    const { taskManager, reportTarget, detail, asyncChildTasks } =
      useTaskContext();
    const appLoading = useAppLoading();
    const isQuotationEnabled = useStoreFeature(StoreFeature.Quotation).enabled;
    const [isQuotationDirty] = useBehaviorSubject(
      taskManager.quotationManager.dirty$,
    );
    const [quotationFormStaged] = useBehaviorSubject(
      taskManager.quotationManager.staged$,
    );

    const productManifestItems = useLocalObservable(
      taskManager.productManifestManager.manifest$,
      [],
    );

    const { canRollback, rollback } = useCanRollbackOrder({
      orderNo: detail.basicInfo.orderNo,
      taskNo: detail.basicInfo.taskNo,
      finishedAt: detail.basicInfo.finishedAt,
      isMainOrder: detail.basicInfo.isMainOrder,
    });

    const extraInfo = useTaskExtraInfo();

    const onViewReport = usePersistFn(() => {
      const projections: {
        [p in ReportTabKey]?: boolean;
      } = {
        preInspection: detail.preInspection.status !== CommonTaskStatus.Pending,
        inspection: detail.inspection.status !== CommonTaskStatus.Pending,
        construction: detail.construction.status !== CommonTaskStatus.Pending,
        deliveryCheck: detail.deliveryCheck.status !== CommonTaskStatus.Pending,
        samplingCheck: detail.samplingCheck.status !== CommonTaskStatus.Pending,
      };

      if (
        detail.basicInfo.hasChildOrders &&
        asyncChildTasks.status === 'success' &&
        asyncChildTasks.result
      ) {
        const childTasks = asyncChildTasks.result;
        projections.preInspection = childTasks.some(
          x => x.preInspectionStatus !== CommonTaskStatus.Pending,
        );
        projections.inspection = childTasks.some(
          x => x.inspectionStatus !== CommonTaskStatus.Pending,
        );
        projections.construction = childTasks.some(
          x => x.constructionStatus !== CommonTaskStatus.Pending,
        );
      }

      navigation.navigate('report', {
        title: '预览报告',
        target: reportTarget,
        productManifest: {
          items$: taskManager.productManifestManager.manifest$,
          onViewDetail: item => {
            navigation.push('_productDetail', {
              id: item.product.id,
            });
          },
        },
        ...projections,
      });
    });

    const onViewTimeline = usePersistFn(() => {
      navigation.navigate('timeline', {});
    });

    const onViewPendingIssues = usePersistFn(() => {
      navigation.navigate('_pendingIssues', {});
    });

    const onViewMedias = usePersistFn(() => {
      navigation.navigate('_mediaAlbums', {});
    });

    const onViewQuotation = useViewQuotation();

    const onViewProductManifest = usePersistFn(() => {
      navigation.navigate('_products', {
        items$: taskManager.productManifestManager.manifest$,
        onRemove: async (item: TaskProductManifestItem) => {
          await removeProductManifestItem(
            taskManager.productManifestManager,
            item.subject,
            item.product.id,
          );
        },
        onEditCloser: (item: TaskProductManifestItem) => {
          navigation.push('_selectCloser', {
            title: '选择成交人',
            onSelect: async (member: OrgMember) => {
              const newCloser = orgMemberToProductAuthor(member);
              navigation.goBack();
              try {
                await taskManager.productManifestManager.changeManifestItemCloser(
                  item.id,
                  newCloser,
                );
                appLoading.show({
                  type: 'success',
                  message: '已更新',
                  duration: 1000,
                });
              } catch (e) {
                console.warn('[product-manifest] change-closer failed', e);
                void showAlert('更改成交人失败', '请稍后重试');
              }
            },
          });
        },
      });
    });

    const onServiceRecordsPress = usePersistFn(() => {
      navigation.navigate('records', {
        licensePlateNo: detail.basicInfo.licensePlateNo,
        vin: detail.basicInfo.vin,
      });
    });

    useEffect(() => {
      const sub = event$.subscribe(event => {
        if (
          event.type === 'rollback' &&
          event.taskNo === detail.basicInfo.taskNo
        ) {
          void taskManager.refresh(true);
        }
      });
      return () => sub.unsubscribe();
    }, [detail.basicInfo.taskNo, event$, taskManager]);

    const iconCount =
      1 /* task events */ +
      1 /* pending issues */ +
      1 /* media albums */ +
      (productManifestItems.length > 0 || kForceProductManifestMenuItem
        ? 1
        : 0) +
      ((isQuotationEnabled && detail.basicInfo.isMainOrder) ||
      kForceQuotationMenuItem
        ? 1
        : 0) +
      1 /* preview report */ +
      (serviceRecordCount > 0 ? 1 : 0) +
      (canRollback ? 1 : 0);

    const iconScale = Math.min(1, 6 / iconCount);

    return (
      <StackLayout
        direction="row"
        justifyContent="space-around"
        alignItems="center"
        gap={0}
        css={`
          background-color: #fff;
          border-radius: ${rpx(10)}px;
          margin-bottom: ${rpx(15)}px;
          padding: ${rpx(15)}px;
          flex-wrap: nowrap;
          overflow: hidden;
        `}
      >
        <MenuItem
          title="工作进度"
          icon={<RecordsIcon size={rpx(44) * iconScale} />}
          onPress={onViewTimeline}
        />
        <MenuItem
          title={
            detail.basicInfo.status === InspectionTaskStatus.Finished
              ? '遗留问题'
              : '历史遗留'
          }
          icon={<PendingIssuesIcon size={rpx(40) * iconScale} />}
          onPress={onViewPendingIssues}
          badge={extraInfo?.issueCount ?? 0}
        />
        <MenuItem
          title="媒体图库"
          icon={<MediasIcon size={rpx(44) * iconScale} />}
          onPress={onViewMedias}
        />
        {(productManifestItems.length > 0 || kForceProductManifestMenuItem) && (
          <MenuItem
            title="产品清单"
            icon={<ProductManifestIcon size={rpx(38) * iconScale} />}
            badge={productManifestItems.length}
            onPress={onViewProductManifest}
          />
        )}
        {((isQuotationEnabled && detail.basicInfo.isMainOrder) ||
          kForceQuotationMenuItem) && (
          <MenuItem
            title="维修报价"
            icon={<QuotationIcon size={rpx(44) * iconScale} />}
            onPress={onViewQuotation}
            badge={
              detail.basicInfo.quotationStatus === CommonTaskStatus.Finished ||
              isQuotationDirty ? (
                <QuotationBadge
                  confirmed={
                    detail.basicInfo.quotationStatus ===
                    CommonTaskStatus.Finished
                  }
                  dirty={isQuotationDirty}
                />
              ) : (
                quotationFormStaged.subjects.length
              )
            }
          />
        )}
        <MenuItem
          title="预览报告"
          icon={<ReportsIcon size={rpx(48) * iconScale} />}
          onPress={onViewReport}
        />
        {serviceRecordCount > 0 && (
          <MenuItem
            title="服务记录"
            icon={<ServiceRecordsIcon size={rpx(41) * iconScale} />}
            onPress={onServiceRecordsPress}
          />
        )}
        {canRollback ? (
          <MenuItem
            title="维修返单"
            icon={<RollbackIcon size={rpx(44) * iconScale} />}
            onPress={rollback}
          />
        ) : null}
      </StackLayout>
    );
  },
);

const QuotationBadge = memo(
  ({ confirmed, dirty }: { confirmed?: boolean; dirty?: boolean }) => {
    const { rpx } = useResponsiveContext();
    return (
      <Center
        css={`
          position: absolute;
          top: ${rpx(-2)}px;
          right: ${rpx(confirmed ? 0 : -4)}px;
          min-width: ${rpx(confirmed ? 16 : 20)}px;
          height: ${rpx(confirmed ? 16 : 20)}px;
          border-radius: ${rpx(20)}px;
          background-color: ${confirmed ? StatusColors.Success : 'transparent'};
        `}
      >
        {confirmed ? (
          <Icon name="check" size={rpx(14)} color={'#fff'} />
        ) : dirty ? (
          <DraftIcon size={rpx(18)} bgFill={StatusColors.Warn} />
        ) : null}
      </Center>
    );
  },
);

const MenuItem = memo(
  ({
    title,
    textStyle,
    icon,
    badge,
    style,
    onPress,
  }: {
    title: ReactNode;
    textStyle?: StyleProp<TextStyle>;
    icon: ReactNode;
    badge?: ReactNode;
    style?: StyleProp<ViewStyle>;
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        style={style}
        onPress={onPress}
        css={`
          justify-content: center;
          align-items: center;
          padding: 0 ${rpx(5)}px;
          flex-grow: 0;
          flex-shrink: 1;
        `}
      >
        <Center
          css={`
            height: ${rpx(48)}px;
          `}
        >
          {icon}
          {badge && (typeof badge === 'number' || typeof badge === 'string') ? (
            <Center
              css={`
                position: absolute;
                top: ${rpx(-4)}px;
                right: ${rpx(-6)}px;
                min-width: ${rpx(20)}px;
                height: ${rpx(20)}px;
                padding: ${rpx(0)}px ${rpx(5)}px;
                border-radius: ${rpx(20)}px;
                background-color: ${badge ? 'red' : 'rgba(0, 0, 0, 0.15)'};
              `}
            >
              <Text
                css={`
                  font-size: ${rpx(10)}px;
                  font-weight: 600;
                  color: #fff;
                `}
              >
                {badge}
              </Text>
            </Center>
          ) : badge ? (
            <>{badge}</>
          ) : null}
        </Center>
        <Center
          css={`
            width: 100%;
            overflow: hidden;
            margin-top: ${rpx(3)}px;
          `}
        >
          <MaybeText
            text={title}
            adjustsFontSizeToFit={true}
            numberOfLines={1}
            style={textStyle}
            css={`
              text-align: center;
              font-family: ${FontFamily.NotoSans.Medium};
              font-size: ${rpx(13)}px;
              color: #777;
            `}
          />
        </Center>
      </TouchableOpacity>
    );
  },
);

const DraftIcon: FC<IconProps & { bgFill?: string }> = memo(props => {
  const {
    fill = '#fff',
    bgFill = '#000',
    ...svgProps
  } = useSvgProps(props, 16, 16);
  return (
    <Svg viewBox="0 0 1024 1024" fill="none" {...svgProps}>
      <Path
        d="M512 512m-512 0a512 512 0 1 0 1024 0 512 512 0 1 0-1024 0Z"
        fill={bgFill}
      />
      <Path
        d="M696.746667 271.018667H439.722667c-26.538667 0-48.213333 21.674667-48.213334 48.213333v8.021333c0 4.778667 3.242667 8.021333 8.021334 8.021334h232.96c26.538667 0 48.213333 21.674667 48.213333 48.213333v249.002667c0 4.778667 3.242667 8.021333 8.021333 8.021333h8.021334c26.538667 0 48.213333-21.674667 48.213333-48.213333v-273.066667c0-26.538667-21.674667-48.213333-48.213333-48.213333zM584.277333 383.488H327.253333c-26.538667 0-48.213333 21.674667-48.213333 48.213333V704.853333c0 26.538667 21.674667 48.213333 48.213333 48.213334h257.024c26.538667 0 48.213333-21.674667 48.213334-48.213334V431.701333c0-26.538667-21.674667-48.213333-48.213334-48.213333z m-240.981333 80.298667c0-8.874667 7.253333-16.042667 16.042667-16.042667h160.682666c8.874667 0 16.042667 7.253333 16.042667 16.042667v16.042666c0 8.874667-7.253333 16.042667-16.042667 16.042667H359.338667c-8.874667 0-16.042667-7.253333-16.042667-16.042667v-16.042666z m192.768 208.896c0 8.874667-7.253333 16.042667-16.042667 16.042666H359.338667c-8.874667 0-16.042667-7.253333-16.042667-16.042666v-16.042667c0-8.874667 7.253333-16.042667 16.042667-16.042667h160.682666c8.874667 0 16.042667 7.253333 16.042667 16.042667v16.042667z m32.170667-96.426667c0 8.874667-7.253333 16.042667-16.042667 16.042667h-192.853333c-8.874667 0-16.042667-7.253333-16.042667-16.042667v-16.042667c0-8.874667 7.253333-16.042667 16.042667-16.042666H552.106667c8.874667 0 16.042667 7.253333 16.042666 16.042666v16.042667z"
        fill={fill}
      />
    </Svg>
  );
});
